import AddExerciseButton from 'components/ExerciseCollection/AddExerciseButton'
import ExerciseCollectionCycle from 'components/ExerciseCollection/ExerciseCollectionCycle'
import ExerciseCollectionWorkoutSlots from 'components/ExerciseCollection/ExerciseCollectionWorkoutSlots'

const ExercisePassesEquipmentFilter = (exerciseCollectionCycleExercise) => {
    return (exerciseCollectionCycleExercise?.equipmentFilterPassed ?? 1) !== 0
}

const ExercisePassesInjuryFilter = (exerciseCollectionCycleExercise) => {
    return (exerciseCollectionCycleExercise?.injuryFilterPassed ?? 1) !== 0
}

// Injury additions always show. Injury removals only show while highlighting injury overrides
const GetVisibleExerciseCollectionCycles = (exerciseCollectionCycles, showEquipmentFilteredExercises, highlightInjuryOverrides) => {
    if (showEquipmentFilteredExercises && highlightInjuryOverrides) {
        return exerciseCollectionCycles
    }

    return exerciseCollectionCycles.flatMap((exerciseCollectionCycle) => {
        if (!showEquipmentFilteredExercises && exerciseCollectionCycle?.equipmentFilterPassed === 0) {
            return []
        }

        if (!highlightInjuryOverrides && exerciseCollectionCycle?.injuryFilterPassed === 0) {
            return []
        }

        const exerciseCollectionCycleExercises = (exerciseCollectionCycle?.exerciseCollectionCycleExercises ?? []).filter((exerciseCollectionCycleExercise) => (
            (showEquipmentFilteredExercises || ExercisePassesEquipmentFilter(exerciseCollectionCycleExercise))
            && (highlightInjuryOverrides || ExercisePassesInjuryFilter(exerciseCollectionCycleExercise))
        ))

        if (exerciseCollectionCycleExercises.length === 0) {
            return []
        }

        return [{
            ...exerciseCollectionCycle,
            exerciseCollectionCycleExercises,
        }]
    })
}

const GetHighlightedExerciseId = (exerciseCollection) => {
    const workoutSlots = (exerciseCollection?.exerciseCollectionCycles ?? []).flatMap((exerciseCollectionCycle) => (
        (exerciseCollectionCycle?.exerciseCollectionCycleExercises ?? []).flatMap((exerciseCollectionCycleExercise) => (
            exerciseCollectionCycleExercise?.workoutSlots ?? []
        ))
    ))

    if (workoutSlots.length === 0) {
        return null
    }

    return workoutSlots[workoutSlots.length - 1]?.exerciseId ?? null
}

const GetExerciseCollectionCycleExerciseCount = (exerciseCollectionCycles) => {
    try {
        let exerciseCollectionCycleExerciseCount = 0

        for (const exerciseCollectionCycle of exerciseCollectionCycles) {
            exerciseCollectionCycleExerciseCount += exerciseCollectionCycle.exerciseCollectionCycleExercises.length
        }

        return exerciseCollectionCycleExerciseCount
    } catch (error) {
        console.error(error)
        return 0
    }
}

export default function ExerciseCollection({
    exerciseHashMap = {},
    programLevelHashMap = {},
    slotIndex = 0,
    exerciseCollection = {},
    setExerciseCollection = () => {},
    allowEditing = false,
    showEquipmentFilteredExercises = false,
    highlightInjuryOverrides = false,
    showWorkoutSlots = false,
}) {

    const requireProgramLevelTagging = exerciseCollection.requireProgramLevelTagging ?? 0
    const requireExerciseCompletionToProgress = exerciseCollection.requireExerciseCompletionToProgress ?? 0
    const programLevelRegression = exerciseCollection.programLevelRegression ?? 0
    const exerciseCollectionCycles = GetVisibleExerciseCollectionCycles(
        exerciseCollection?.exerciseCollectionCycles ?? [],
        showEquipmentFilteredExercises,
        highlightInjuryOverrides,
    )
    const exerciseCollectionCycleCount = exerciseCollectionCycles.length ?? 0
    const exerciseCollectionCycleExerciseCount = GetExerciseCollectionCycleExerciseCount(exerciseCollectionCycles)
    const highlightedExerciseId = GetHighlightedExerciseId(exerciseCollection)

    return (
        <div className='relative' data-canvas-block-scroll="true">
            <div className='flex pt-2 px-2 gap-x-2 items-center'>
                <p className='text-sm text-gray-600 flex-1'>
                    Slot {slotIndex + 1} - {exerciseCollection.name} {exerciseCollection?.description && `- ${exerciseCollection.description}`}
                </p>

                <p className='text-sm text-gray-600'>
                    {exerciseCollectionCycleExerciseCount} exercises
                </p>

                {/* 
                {requireProgramLevelTagging === 1 && (
                    <p className='text-xs text-gray-600'>Program Level Tagging Required</p>
                )} 
                */}

                {requireExerciseCompletionToProgress === 1 && (
                    <p className='text-xs text-gray-600 font-bold'>(Completions Required to Progress)</p>
                )}

                {programLevelRegression > 0 && (
                    <p className='text-xs text-gray-600 font-bold' data-testid='exercise-collection-program-level-regression'>
                        (Starting Point Regressed {programLevelRegression} {programLevelRegression === 1 ? 'Level' : 'Levels'})
                    </p>
                )}
            </div>

            <div className='w-full px-4 py-2 overflow-x-scroll [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'>
                {exerciseCollectionCycleCount === 0 && allowEditing && (
                    <div className='flex w-max max-w-none gap-x-2 items-start h-[320px]'>
                        <AddExerciseButton 
                            exerciseCollection={exerciseCollection}
                            setExerciseCollection={setExerciseCollection}
                            insertIndex={0} 
                        />
                    </div>
                )}

                {exerciseCollectionCycleCount > 0 && (
                    <div className='flex w-max max-w-none gap-x-2 items-start'>
                        {allowEditing && (
                            <AddExerciseButton 
                                exerciseCollection={exerciseCollection}
                                setExerciseCollection={setExerciseCollection}
                                insertIndex={0} 
                            />
                        )}

                        {exerciseCollectionCycles.map((exerciseCollectionCycle, exerciseCollectionCycleIndex) => (
                            <ExerciseCollectionCycle
                                key={exerciseCollectionCycleIndex}
                                exerciseHashMap={exerciseHashMap}
                                programLevelHashMap={programLevelHashMap}
                                exerciseCollection={exerciseCollection}
                                setExerciseCollection={setExerciseCollection}
                                requireProgramLevelTagging={requireProgramLevelTagging}
                                exerciseCollectionCycleIndex={exerciseCollectionCycleIndex}
                                exerciseCollectionCycle={exerciseCollectionCycle}
                                allowEditing={allowEditing}
                                highlightedExerciseId={highlightedExerciseId}
                                highlightInjuryOverrides={highlightInjuryOverrides}
                            />
                        ))}

                        {allowEditing && (
                            <AddExerciseButton 
                                exerciseCollection={exerciseCollection}
                                setExerciseCollection={setExerciseCollection}
                                insertIndex={exerciseCollectionCycles.length} 
                            />
                        )}
                    </div>
                )}
            </div>

            {showWorkoutSlots && (
                <ExerciseCollectionWorkoutSlots exerciseCollection={exerciseCollection} />
            )}
        </div>
    )
}